import { useState } from 'react'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { MinimalButton, MinimalInput } from '@ovasabi/ui-minimal'
import { ChowModal, CityPicker } from '../../components/chow'
import { PinIcon } from '../../components/icons'
import type { DeliveryLocation } from '../../lib/deliveryLocation'
import { cityLabel, useCities, type City } from '../../lib/places'

const Style = {
  Container: styled.div`
    display: flex;
    flex-direction: column;
    gap: ${minimalVars.space.sm};
  `,
  IntroText: styled.p`
    margin: 0;
    font-size: ${minimalVars.typography.captionSize};
    color: ${minimalVars.color.textSecondary};
    line-height: 1.45;
  `,
  DetectBtn: styled(MinimalButton)`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${minimalVars.space.xs};
    width: 100%;
    padding: 10px 14px;
    border-radius: ${minimalVars.radius.md};
    border: 1px solid ${minimalVars.color.brandSoft};
    background: ${minimalVars.color.brandSoft};
    color: ${minimalVars.color.brand};
    font-size: ${minimalVars.typography.captionSize};
    font-weight: 700;
    cursor: pointer;
    transition: filter 140ms ease, opacity 140ms ease;
    &:hover:not(:disabled) {
      filter: brightness(0.96);
    }
    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  `,
  StatusNote: styled.p`
    margin: 0;
    font-size: ${minimalVars.typography.metaSize};
    color: ${minimalVars.color.warning};
    font-weight: 600;
  `,
  Actions: styled.div`
    display: flex;
    justify-content: flex-end;
  `,
  SaveBtn: styled(MinimalButton)`
    padding: ${minimalVars.space.xs} ${minimalVars.space.sm};
    border-radius: ${minimalVars.radius.md};
    border: none;
    background: ${minimalVars.color.brand};
    color: ${minimalVars.color.textInverse};
    font-weight: 700;
    font-size: ${minimalVars.typography.captionSize};
    cursor: pointer;
    &:hover:not(:disabled) {
      filter: brightness(0.92);
    }
    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  `,
}

export interface DeliveryLocationModalProps {
  open: boolean
  onClose: () => void
  location: DeliveryLocation
}

/** The neighbourhood part of a stored "Wuse II, Abuja" area, if any. */
const neighbourhoodOf = (area: string, city: City | undefined): string => {
  if (!city) return ''
  const [head, ...rest] = area.split(',')
  return rest.length > 0 && head.trim().toLowerCase() !== city.name.toLowerCase() ? head.trim() : ''
}

export const DeliveryLocationModal = ({ open, onClose, location }: DeliveryLocationModalProps) => {
  const { byRef } = useCities()
  const current = byRef.get(location.cityRef)
  const [picked, setPicked] = useState<City | undefined>()
  const [neighbourhood, setNeighbourhood] = useState<string | undefined>()
  const city = picked ?? current
  const place = neighbourhood ?? neighbourhoodOf(location.area, current)

  const apply = () => {
    if (!city) return
    location.setCity(city, place)
    setPicked(undefined)
    setNeighbourhood(undefined)
    onClose()
  }

  return (
    <ChowModal
      open={open}
      title="Find kitchens near you"
      onClose={onClose}
      maxWidth={480}
    >
      <Style.Container>
        <Style.IntroText>
          Choose your city to see the kitchens closest to you first. Distances are between city centres; you collect
          orders from the address shown at checkout.
        </Style.IntroText>

        <Style.DetectBtn variant="quiet"
          type="button"
          onClick={() => {
            location.detect()
          }}
          disabled={location.locating === 'locating'}
        >
          <PinIcon size="sm" />
          {location.locating === 'locating' ? 'Locating your city…' : 'Use my current GPS location'}
        </Style.DetectBtn>

        {location.locating === 'denied' && (
          <Style.StatusNote>
            Location permission was denied. Choose your city below.
          </Style.StatusNote>
        )}
        {location.locating === 'unavailable' && (
          <Style.StatusNote>
            Could not identify a supported city from your location. Choose your city below.
          </Style.StatusNote>
        )}

        <CityPicker
          label="Your city"
          value={city?.ref ?? ''}
          hint={!city && location.area ? `Showing kitchens for “${location.area}”.` : undefined}
          onChange={setPicked}
        />
        <MinimalInput
          label="Neighbourhood (optional)"
          placeholder="e.g. Wuse II, Lekki Phase 1"
          value={place}
          onChange={(e) => setNeighbourhood(e.target.value)}
        />
        <Style.Actions>
          <Style.SaveBtn type="button" onClick={apply} disabled={!city}>
            {city ? `Show kitchens near ${cityLabel(city)}` : 'Choose a city'}
          </Style.SaveBtn>
        </Style.Actions>
      </Style.Container>
    </ChowModal>
  )
}
